import { ReactNode } from 'react';

export const PLAYER_CIRCLE_SIZE = 60;

// Corner controls (club badge / remove X) share one size; the centre
// plus/spinner is a touch larger to read as the primary affordance.
export const CONTROL_SIZE = 24;
const CENTRE_SIZE = 28;

/**
 * Blur applied to the *contents* of a withheld player token — portrait and
 * name — while every frame around them stays sharp (FFH-2409). Shared so the
 * surfaces cannot drift out of step with each other.
 *
 * Enough to make a name illegible and a face unplaceable at this size, while
 * leaving the silhouette and the kit colour to look at. Anything heavier stops
 * reading as a hidden player.
 */
export const CONTENT_BLUR = 'blur-[5px]';

// TEMP TEST asset — replace with the dedicated silhouette export. Served
// same-origin from /public so the CSS mask isn't blocked by CORS.
const SILHOUETTE_URL = '/images/player-silhouette-test.png';
const SILHOUETTE_GREY = '#C8C8C8';
const ACCENT_BLUE = '#47B8EC'; // primary-400

/**
 * Flattens a cut-out portrait to the same flat grey as `SILHOUETTE_GREY`,
 * keeping its alpha and so its outline: `brightness(0)` takes every visible
 * pixel to black, and `invert(78.4%)` lifts that black to #C8C8C8 (200/255).
 *
 * A filter rather than a CSS mask of the portrait, because a mask would need
 * the image to be same-origin and these are served from the player CDN — the
 * very constraint that has the shared silhouette above living in /public.
 */
export const SILHOUETTE_FILTER = 'brightness(0) invert(78.4%)';

// Shared mask that cuts a div down to the silhouette shape; the visible colour
// is whatever `backgroundColor` the layer sets.
const SILHOUETTE_MASK_STYLE = {
  WebkitMaskImage: `url(${SILHOUETTE_URL})`,
  maskImage: `url(${SILHOUETTE_URL})`,
  WebkitMaskSize: 'cover',
  maskSize: 'cover',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat'
} as const;

/**
 * Circular avatar frame: a light-grey player silhouette (a CSS mask of a
 * cut-out portrait) on white, with a 1px grey stroke. Children layer on top
 * (portrait, centre plus, spinner, …). When `active` (the slot is being
 * edited), a blue silhouette pulses over the grey one (grey → blue → grey).
 */
export const PlayerCircle = ({
  size = PLAYER_CIRCLE_SIZE,
  hideSilhouette = false,
  active = false,
  selected = false,
  children
}: {
  size?: number;
  hideSilhouette?: boolean;
  active?: boolean;
  /** Highlight the portrait border in primary blue (interactive selection). */
  selected?: boolean;
  children?: ReactNode;
}) => (
  <div
    className={`relative rounded-full overflow-hidden bg-white ${
      selected ? 'border-2 border-primary-500' : 'border border-neutral-300'
    }`}
    style={{ width: size, height: size }}
  >
    <div
      aria-hidden
      className={`absolute inset-0 transition-opacity duration-300 ${
        hideSilhouette ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: SILHOUETTE_GREY, ...SILHOUETTE_MASK_STYLE }}
    />
    {active && (
      <div
        aria-hidden
        className="absolute inset-0 animate-silhouette-pulse"
        style={{ backgroundColor: ACCENT_BLUE, ...SILHOUETTE_MASK_STYLE }}
      />
    )}
    {children}
  </div>
);

const CentreOverlay = ({ children }: { children: ReactNode }) => (
  <div className="absolute inset-0 z-10 flex items-center justify-center">
    {children}
  </div>
);

/** Blue '+' shown on empty slots. */
export const AddPlusOverlay = () => (
  <CentreOverlay>
    <div
      className="flex items-center justify-center rounded-full"
      style={{
        width: CENTRE_SIZE,
        height: CENTRE_SIZE,
        backgroundColor: ACCENT_BLUE
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </div>
  </CentreOverlay>
);

/** Blue spinner (same size/colour as the plus) shown while a portrait loads. */
export const LoadingOverlay = () => (
  <CentreOverlay>
    <div
      className="animate-spin rounded-full border-2"
      style={{
        width: CENTRE_SIZE,
        height: CENTRE_SIZE,
        borderColor: 'rgba(71, 184, 236, 0.3)',
        borderTopColor: ACCENT_BLUE
      }}
    />
  </CentreOverlay>
);

/** Club crest in a circle matched to the remove-button size, sized by height. */
export const ClubBadgeCircle = ({ teamCode }: { teamCode: string }) => (
  <div
    className="flex items-center justify-center rounded-full overflow-hidden bg-white border border-neutral-300 p-0.5"
    style={{ width: CONTROL_SIZE, height: CONTROL_SIZE }}
  >
    <img
      src={`/badges/${teamCode}.png`}
      alt={teamCode}
      className="max-h-full max-w-full object-contain"
    />
  </div>
);

/**
 * Padlock in a circle matched to the club badge, taking the badge's corner on a
 * withheld player (FFH-2409). Sharp, unlike everything it sits beside: it is
 * the one part of the token that is not the hidden player, and it is what says
 * the blur is a lock rather than a loading state.
 */
export const LockCircle = () => (
  <div
    className="flex items-center justify-center rounded-full overflow-hidden bg-white border border-neutral-300 p-0.5"
    style={{ width: CONTROL_SIZE, height: CONTROL_SIZE }}
  >
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5 text-neutral-400"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="9" rx="2" fill="currentColor" />
      <path
        d="M8 11V8a4 4 0 0 1 8 0v3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  </div>
);
